import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { billingApi } from "@/lib/sprint2Entities";
import { contractApi } from "@/lib/phase3Entities";
import { apiError } from "@/lib/errors";

const fmtMoney = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
};

export default function BillingTab({ tourId }) {
  const [b, setB] = useState(null);

  const load = async () => {
    try { setB(await billingApi.forTour(tourId)); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tourId]);

  const markPaid = async (rowId) => {
    if (!b?.contract_id) return;
    try { await contractApi.markPaid(b.contract_id, rowId, null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!b || !b.contract_id) {
    return (
      <div className="bg-white border border-coach-silver rounded-md p-6 text-center text-steel text-[13px]"
        data-testid="billing-tab">
        No contract yet. Billing appears once a contract is generated.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="billing-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Contract Total" value={fmtMoney(b.contract_total)} big />
        <Tile label="Deposit" value={fmtMoney(b.deposit_amount)} />
        <Tile label="Paid" value={fmtMoney(b.paid)} color="text-touring-lime" />
        <Tile label="Outstanding" value={fmtMoney(b.outstanding)} color="text-amber-hold" />
      </div>

      {b.next_payment && (
        <div className="border-l-2 border-coach-blue bg-road-white p-3 text-[13px]" data-testid="billing-next-payment">
          <div className="text-[10.5px] uppercase tracking-widest text-steel">Next Payment</div>
          <div className="mt-1 text-carbon">
            <strong>{b.next_payment.label}</strong> · {fmtMoney(b.next_payment.scheduled_amount)}
            {b.next_payment.due_date && <span className="text-steel"> · due {b.next_payment.due_date}</span>}
          </div>
        </div>
      )}

      <div className="bg-white border border-coach-silver rounded-md">
        <div className="px-4 py-2 text-[10.5px] uppercase tracking-widest text-steel border-b border-coach-silver/60">
          Payment Schedule
        </div>
        <table className="w-full text-[13px]">
          <thead className="bg-road-white text-[10.5px] uppercase tracking-widest text-steel">
            <tr>
              <th className="text-left px-3 py-2">Milestone</th>
              <th className="text-left px-3 py-2 w-32">Due</th>
              <th className="text-right px-3 py-2 w-28">Scheduled</th>
              <th className="text-right px-3 py-2 w-28">Paid</th>
              <th className="text-left px-3 py-2 w-24">Status</th>
              <th className="w-24" />
            </tr>
          </thead>
          <tbody>
            {(b.payment_schedule || []).map((r) => (
              <tr key={r.id} className="border-t border-coach-silver/60" data-testid={`billing-row-${r.id}`}>
                <td className="px-3 py-2 text-carbon">{r.label}</td>
                <td className="px-3 py-2 text-steel">{r.due_date || "—"}</td>
                <td className="px-3 py-2 text-carbon text-right">{fmtMoney(r.scheduled_amount)}</td>
                <td className="px-3 py-2 text-carbon text-right">{r.paid_amount ? fmtMoney(r.paid_amount) : "—"}</td>
                <td className="px-3 py-2 text-steel">{r.status}</td>
                <td className="px-3 py-2 text-right">
                  {r.status !== "paid" && (
                    <button onClick={() => markPaid(r.id)}
                      className="text-coach-blue hover:underline text-[12px]"
                      data-testid={`billing-mark-paid-${r.id}`}>Mark Paid</button>
                  )}
                </td>
              </tr>
            ))}
            {(!b.payment_schedule || b.payment_schedule.length === 0) && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-steel">No payments scheduled.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Tile = ({ label, value, big, color = "text-carbon" }) => (
  <div className="bg-white border border-coach-silver rounded-md p-4">
    <div className="text-[10.5px] uppercase tracking-widest text-steel">{label}</div>
    <div className={`${big ? "font-display text-3xl" : "text-[16px] font-medium"} ${color} leading-tight mt-1`}>{value}</div>
  </div>
);
